import React, { useEffect, useRef } from 'react';
import { StarIcon, CheckCircleIcon, ChevronRightIcon } from 'lucide-react';
import JsBarcode from 'jsbarcode';
interface LoyaltyData {
  memberId?: string;
  pointsBalance?: number;
  status?: 'active' | 'inactive';
  tier?: 'silver' | 'gold' | 'platinum';
  isEnrolled?: boolean;
}
interface LoyaltyCardProps {
  progress?: number;
  onJoinNow?: () => void;
  onViewDetails?: () => void;
  loyaltyData?: LoyaltyData;
  variant?: 'default' | 'compact';
}
export function LoyaltyCard({
  progress = 0,
  onJoinNow,
  onViewDetails,
  loyaltyData,
  variant = 'default'
}: LoyaltyCardProps) {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const isEnrolled = loyaltyData?.isEnrolled && progress === 100;
  // Generate barcode when in member state
  useEffect(() => {
    if (isEnrolled && loyaltyData?.memberId && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, loyaltyData.memberId, {
          format: 'CODE128',
          width: 2.5,
          height: 80,
          displayValue: false,
          background: '#ffffff',
          lineColor: '#000000',
          margin: 10
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [isEnrolled, loyaltyData?.memberId]);
  const getTierColor = (tier?: string) => {
    switch (tier) {
      case 'platinum':
        return 'text-gray-300';
      case 'gold':
        return 'text-yellow-300';
      case 'silver':
        return 'text-gray-400';
      default:
        return 'text-blue-200';
    }
  };
  const getTierLabel = (tier?: string) => {
    if (!tier) return 'Member';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };
  // Member Summary State
  if (isEnrolled) {
    if (variant === 'compact') {
      return <button onClick={onViewDetails} className="w-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-2.5 text-white shadow-md flex items-center justify-between" data-id="element-456" data-mp="30bvdxtqvfb2">
          <div className="flex items-center gap-3" data-id="element-457" data-mp="6hzmtrav09j2">
            <div className="w-7 h-7 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-458" data-mp="6ju7r5m4s1n5">
              <StarIcon className="w-3.5 h-3.5 text-[#1E2B55]" fill="currentColor" data-id="element-459" data-mp="1hv6c19ybesu" />
            </div>
            <div className="text-left" data-id="element-460" data-mp="3ooe1jsgycnii">
              <p className="text-xs font-bold leading-tight" data-id="element-461" data-mp="3opp1tib6vd4v">Theqh Points</p>
              <p className="text-sm font-black text-[#FFD700] leading-tight" data-id="element-462" data-mp="p7piegmg4zc1">
                {loyaltyData?.pointsBalance?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
          <div className="text-right" data-id="element-463" data-mp="fhw61dehcg6q">
            <p className="text-[10px] text-blue-200 uppercase font-bold tracking-wider leading-tight" data-id="element-464" data-mp="fguvda1dlvpj">
              {getTierLabel(loyaltyData?.tier)}
            </p>
            <ChevronRightIcon className="w-4 h-4 text-white ml-auto opacity-70" data-id="element-465" data-mp="1801qzl3p0wum" />
          </div>
        </button>;
    }
    return <div onClick={onViewDetails} className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-4 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" data-id="element-466" data-mp="136p1z9v3qo1c">
        <div className="flex items-center gap-2 mb-4" data-id="element-467" data-mp="2qw7jdibk5f3p">
          <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center" data-id="element-468" data-mp="2uv6pv7cxtjx9">
            <StarIcon className="w-5 h-5 text-[#1E2B55]" fill="currentColor" data-id="element-469" data-mp="2zvudrk5vdkn9" />
          </div>
          <div className="flex-1" data-id="element-470" data-mp="34t2tle3jpclc">
            <h3 className="text-base font-semibold" data-id="element-471" data-mp="34s1jpcy9mw57">Theqh Points</h3>
            <div className="flex items-center gap-1 mt-0.5" data-id="element-472" data-mp="39rxoheeserlt">
              <CheckCircleIcon className="w-3 h-3 text-green-400" data-id="element-473" data-mp="3ek786cpb6vfn" />
              <span className="text-xs text-green-300" data-id="element-474" data-mp="3em1vtyryt1nf">Active Member</span>
            </div>
          </div>
          <ChevronRightIcon className="w-5 h-5 text-white opacity-70" data-id="element-475" data-mp="3indd9h0oqgkj" />
        </div>
        {/* Member Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-476" data-mp="3sjgpxgt5xxlq">
          <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm" data-id="element-477" data-mp="3sjzy45t0h7mk">
            <p className="text-xs text-blue-200 mb-1" data-id="element-478" data-mp="3evoqjqslnvhd">Points Balance</p>
            <p className="text-xl font-bold text-[#FFD700]" data-id="element-479" data-mp="39yg4hf4bz7au">
              {loyaltyData?.pointsBalance?.toLocaleString() || '0'}
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm" data-id="element-480" data-mp="3oovwpdv5zfii">
            <p className="text-xs text-blue-200 mb-1" data-id="element-481" data-mp="3johuwdk5p1sx">Membership Tier</p>
            <p className={`text-xl font-bold ${getTierColor(loyaltyData?.tier)}`} data-id="element-482" data-mp="2vz6uix6px9fb">
              {getTierLabel(loyaltyData?.tier)}
            </p>
          </div>
        </div>
        {/* Status */}
        <div className="mb-4" data-id="element-483" data-mp="35xml8z1u2goa">
          <div className="flex items-center justify-between text-xs" data-id="element-484" data-mp="30x8avacreun3">
            <span className="text-blue-200" data-id="element-485" data-mp="30vx91g2vjwym">Status</span>
            <span className={`font-semibold ${loyaltyData?.status === 'active' ? 'text-green-300' : 'text-red-300'}`} data-id="element-486" data-mp="l5ayume8ofdd">
              {loyaltyData?.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        {/* Barcode Section */}
        {loyaltyData?.memberId && <div className="bg-white rounded-lg p-4 text-center" data-id="element-487" data-mp="sqwpsrolcmey">
            <div className="flex items-center justify-center mb-3" data-id="element-488" data-mp="eqvnr8wlb8xw">
              <svg ref={barcodeRef} className="max-w-full h-auto" data-id="element-489" data-mp="ir5ski8nbtb6"></svg>
            </div>
            <p className="text-sm text-gray-700 font-mono tracking-wider" data-id="element-490" data-mp="16h048wh8bbtu">
              Member ID: {loyaltyData.memberId}
            </p>
          </div>}
      </div>;
  }
  // Onboarding State (existing behavior)
  if (variant === 'compact') {
    return <button onClick={onJoinNow} className="w-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-2.5 text-white shadow-md flex items-center justify-between" data-id="element-491" data-mp="xog0xipzsj2o">
        <div className="flex items-center gap-3" data-id="element-492" data-mp="1peb5gmmf8fbx">
          <div className="w-7 h-7 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-493" data-mp="1u6ce7ykdrnx7">
            <StarIcon className="w-3.5 h-3.5 text-[#1E2B55]" fill="currentColor" data-id="element-494" data-mp="242umrtwne4i6" />
          </div>
          <div className="text-left" data-id="element-495" data-mp="201hj9qh12def">
            <p className="text-xs font-bold leading-tight" data-id="element-496" data-mp="1v1wj5cq4fz04">
              Join Theqh Points
            </p>
            <p className="text-[10px] text-blue-100 leading-tight mt-0.5" data-id="element-497" data-mp="1q31wu0sy3sb9">
              Earn points on every order
            </p>
          </div>
        </div>
        <ChevronRightIcon className="w-4 h-4 text-white opacity-70" data-id="element-498" data-mp="1l508p1gnx2sx" />
      </button>;
  }
  return <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-4 text-white" data-id="element-499" data-mp="1gavwo3sji4kz">
      <div className="flex items-center gap-2 mb-3" data-id="element-500" data-mp="1cbn77b4j8sv8">
        <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center" data-id="element-501" data-mp="17d3gyyiwoux2">
          <StarIcon className="w-5 h-5 text-[#1E2B55]" fill="currentColor" data-id="element-502" data-mp="12dqy0ov5o7zr" />
        </div>
        <h3 className="text-base font-bold" data-id="element-503" data-mp="xee5polim8ps">Theqh Points</h3>
      </div>
      <p className="text-xs text-blue-100 mb-3" data-id="element-504" data-mp="xggxoe68abx5">Profile {progress}% complete</p>
      <div className="mb-4" data-id="element-505" data-mp="n9xux9mw7hsu">
        <div className="flex justify-between items-center mb-2" data-id="element-506" data-mp="s9aenligw8r7">
          <span className="text-xs text-blue-100" data-id="element-507" data-mp="s7fu48b3yinn">Progress</span>
          <span className="text-sm font-bold text-[#FFD700]" data-id="element-508" data-mp="x83vnnwyqhw4">{progress}%</span>
        </div>
        <div className="w-full h-2 bg-white bg-opacity-20 rounded-full overflow-hidden" data-id="element-509" data-mp="127q8hw8vpw4e">
          <div className="h-full bg-[#FFD700] transition-all duration-500" style={{
          width: `${progress}%`
        }} data-id="element-510" data-mp="4k81oqys3431" />
        </div>
      </div>
      <p className="text-xs text-blue-100 mb-3" data-id="element-511" data-mp="ei5l915p0cpb">
        Complete your profile to unlock full benefits
      </p>
      <button onClick={onJoinNow} className="w-full bg-white text-[#1E2B55] text-sm font-bold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-512" data-mp="j8ueakx0thqk">
        {progress === 0 ? 'Join Now' : 'Complete Profile'}
      </button>
    </div>;
}